import { Link } from "react-router-dom";
import {
  Fingerprint,
  Cpu,
  ShieldCheck,
  ArrowUpRight,
  ArrowRight,
  Layers,
  Lock,
  KeyRound,
  AlertTriangle,
  Target,
  EyeOff,
  Network,
  CheckCircle2,
  FileText,
  Activity,
  Building2,
  Landmark,
  Coins,
  Banknote,
  Briefcase,
  ShieldHalf,
  Globe,
  CircuitBoard,
  Award,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

import Reveal from "@/components/Reveal";
import FingerprintOrb from "@/components/FingerprintOrb";
import HeroArchitecture from "@/components/HeroArchitecture";
import InfrastructureVideo from "@/components/InfrastructureVideo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ASSETS } from "@/lib/assets";

const Overline = ({ children, dark = false, testid }) => (
  <p
    data-testid={testid}
    className={`text-xs md:text-sm uppercase tracking-[0.22em] font-semibold ${
      dark ? "text-white/70" : "text-[#06102A]/65"
    }`}
  >
    {children}
  </p>
);

const SectionHeader = ({ kicker, title, sub, dark = false, align = "left" }) => (
  <div className={`max-w-7xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <Reveal>
      <Overline dark={dark}>{kicker}</Overline>
    </Reveal>
    <Reveal delay={80}>
      <h2
        className={`mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] ${
          dark ? "text-white" : "text-[#06102A]"
        }`}
      >
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={140}>
        <p
          className={`mt-6 text-base md:text-lg leading-relaxed ${
            dark ? "text-white/70" : "text-[#06102A]/70"
          }`}
        >
          {sub}
        </p>
      </Reveal>
    )}
  </div>
);

/* ---------- 1. HERO ---------- */
const Hero = () => (
  <section className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden bg-radial-soft" data-testid="hero-section">
    <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
    <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <Overline testid="hero-kicker">Identity-Bound Custody Infrastructure</Overline>
          </Reveal>
          <Reveal delay={80}>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A]"
            >
              The Trust Layer for{" "}
              <span className="relative inline-block">
                Regulated
                <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-gradient-to-r from-[#3B82F6] via-[#06102A] to-[#F97316] rounded-full" />
              </span>{" "}
              Digital Finance
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-7 text-lg md:text-xl text-[#06102A]/70 max-w-2xl leading-relaxed">
              Identity-bound custody infrastructure for banks, institutions, and the future of
              digital ownership.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-4 text-base text-[#06102A]/60 max-w-2xl leading-relaxed">
              Vaultavo replaces fragile secrets with biometric identity, hardware-segregated
              custody, and policy-enforced governance — giving regulated institutions a safer way
              to secure and scale digital assets.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-demo"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#06102A] text-white text-sm md:text-base font-medium px-7 py-4 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_18px_40px_-12px_rgba(6,16,42,0.4)]"
              >
                Request a Demo
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/technology"
                data-testid="hero-cta-architecture"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#06102A] text-sm md:text-base font-medium px-7 py-4 border border-[#06102A]/15 hover:border-[#06102A] hover:bg-[#06102A]/[0.03] transition-all duration-300"
              >
                Explore the Architecture
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-14 grid grid-cols-3 gap-6 md:gap-10 max-w-xl">
              {[
                { k: "EAL6+", v: "Secure element" },
                { k: "0", v: "Seed phrases" },
              ].map((m) => (
                <div key={m.k}>
                  <p className="font-display text-2xl md:text-3xl font-medium text-[#06102A] tracking-tight">
                    {m.k}
                  </p>
                  <p className="mt-1 text-xs text-[#06102A]/60">{m.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Visual */}
        <div className="lg:col-span-5 relative">
          <Reveal delay={120}>
            <HeroArchitecture />
          </Reveal>
        </div>
      </div>

      {/* Trusted-by strip */}
      <Reveal delay={280}>
        <div className="mt-20 md:mt-28 border-t border-[#06102A]/10 pt-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#06102A]/50">
            Designed for · Banks · Exchanges · Asset Managers · Tokenization Platforms ·
            Stablecoin Issuers · Regulated Custodians · Governments
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- 2. PROBLEM ---------- */
const Problem = () => {
  const items = [
    { icon: KeyRound, title: "Seed phrases", text: "Can be lost, stolen, or coerced." },
    { icon: EyeOff, title: "Software wallets", text: "Create operational and impersonation risk." },
    { icon: Layers, title: "Pooled custody", text: "Concentrates systemic exposure." },
    {
      icon: AlertTriangle,
      title: "MPC alone",
      text: "Solves part of the problem — not identity, segregation, or governance.",
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          kicker="The Problem"
          title="Digital Finance Cannot Scale on Fragile Secrets"
          sub="Private keys, passwords, seed phrases, and software controls were never built for regulated financial infrastructure."
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 80}>
              <div className="h-full rounded-2xl border border-[#06102A]/10 bg-white p-7 hover:border-[#06102A]/30 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-[#06102A]/[0.04] grid place-items-center text-[#06102A]">
                  <it.icon className="h-5 w-5" />
                </div>
                <p className="mt-6 font-display text-lg font-medium text-[#06102A]">{it.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#06102A]/65">{it.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={300}>
          <div className="mt-12 flex items-start gap-4 max-w-3xl">
            <div className="mt-1 h-1 w-12 bg-[#F97316] rounded-full shrink-0" />
            <p className="text-base md:text-lg text-[#06102A]/80 leading-relaxed">
              Modern attackers do not need to break cryptography. They break human trust.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- 3. PARADIGM SHIFT ---------- */
const ParadigmShift = () => {
  const pillars = [
    {
      icon: Fingerprint,
      title: "Identity replaces secrets",
      text: "Ownership is bound to verified human presence — not passwords or seed phrases.",
    },
    {
      icon: Cpu,
      title: "Hardware enforces control",
      text: "Keys are generated, stored, and accessed inside isolated secure hardware.",
    },
    {
      icon: ShieldCheck,
      title: "Policy governs behaviour",
      text: "Rules, approvals, limits, and audit trails are defined and enforced before signing.",
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-[#FAFBFC]" data-testid="paradigm-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          kicker="The Paradigm Shift"
          title="From Fragile Secrets to Identity-Bound Custody"
          sub="Digital ownership becomes institutional when identity, hardware, and policy work together."
        />
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 100}>
              <div className="group relative h-full rounded-3xl bg-white border border-[#06102A]/10 p-8 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(6,16,42,0.25)] transition-all duration-500">
                <div
                  className="absolute -top-px left-8 right-8 h-px"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(59,130,246,0.6), transparent)",
                  }}
                />
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-xl bg-[#06102A] text-white grid place-items-center">
                    <p.icon className="h-5 w-5" strokeWidth={1.7} />
                  </div>
                  <span className="font-mono-vt text-xs text-[#06102A]/50">0{i + 1}</span>
                </div>
                <h3 className="mt-8 font-display text-2xl md:text-[26px] font-medium text-[#06102A] leading-tight">
                  {p.title}
                </h3>
                <p className="mt-4 text-[15px] text-[#06102A]/70 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- 4. ARCHITECTURE ---------- */
const Architecture = () => {
  const layers = [
    {
      key: "card",
      kicker: "Layer 01 · Identity",
      title: "Vaultavo Biometric Security Device",
      tagline: "Where identity becomes control.",
      bullets: [
        "On-device fingerprint verification",
        "EAL6+ secure element",
        "No cloud biometrics",
        "No seed phrases",
        "Physical user presence required",
        "Currently deployed as Vaultavo's flagship biometric smart card form factor",
      ],
      image: ASSETS.cardNavy,
    },
    {
      key: "vault",
      kicker: "Layer 02 · Hardware",
      title: "MegaVault™",
      tagline: "True hardware segregation for institutional custody.",
      bullets: [
        "Isolated secure-chip array",
        "Per-user key segregation",
        "No pooled custody",
        "No zone keys",
        "Hardware-to-hardware encrypted communication",
        "No single breach can compromise all assets",
      ],
      image: ASSETS.megaVault,
    },
    {
      key: "platform",
      kicker: "Layer 03 · Policy",
      title: "Vaultavo Digital Platform",
      tagline: "The control plane for policy and governance.",
      bullets: [
        "Defines initiators, verifiers, and approvers",
        "Sets limits and workflows",
        "Maintains audit trails",
        "Integrates KYC, AML, and Travel Rule workflows",
        "Never stores private keys",
        "Never stores biometrics — never signs transactions",
      ],
      image: ASSETS.platform,
    },
  ];

  const [active, setActive] = useState("card");
  const current = layers.find((l) => l.key === active);

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="architecture-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          kicker="Vaultavo Architecture"
          title="Three Layers of Trust. One Unified System."
          sub="A transaction can only occur when identity, hardware, and policy agree."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Layer selector */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            {layers.map((l, i) => {
              const isActive = l.key === active;
              return (
                <button
                  key={l.key}
                  onClick={() => setActive(l.key)}
                  data-testid={`architecture-layer-${l.key}`}
                  className={`text-left rounded-2xl border p-6 transition-all duration-500 ${
                    isActive
                      ? "border-[#06102A] bg-[#06102A] text-white shadow-[0_30px_60px_-30px_rgba(6,16,42,0.5)]"
                      : "border-[#06102A]/10 bg-white text-[#06102A] hover:border-[#06102A]/30"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${
                        isActive ? "text-white/70" : "text-[#06102A]/55"
                      }`}
                    >
                      {l.kicker}
                    </span>
                    <span
                      className={`font-mono-vt text-xs ${
                        isActive ? "text-white/60" : "text-[#06102A]/40"
                      }`}
                    >
                      0{i + 1}/03
                    </span>
                  </div>
                  <h3 className="mt-3 font-display text-2xl md:text-[28px] font-medium leading-tight">
                    {l.title}
                  </h3>
                  <p className={`mt-2 text-sm ${isActive ? "text-white/75" : "text-[#06102A]/60"}`}>
                    {l.tagline}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-[#06102A]/10 metallic-panel min-h-[520px]">
              <div className="absolute inset-0 bg-grid opacity-50" />
              <div className="relative p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 h-full">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#06102A]/60">
                    {current.kicker}
                  </p>
                  <h4 className="mt-3 font-display text-3xl md:text-4xl font-medium leading-tight text-[#06102A]">
                    {current.title}
                  </h4>
                  <p className="mt-3 text-[#06102A]/70">{current.tagline}</p>

                  <ul className="mt-7 space-y-3">
                    {current.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-[#06102A]/85">
                        <CheckCircle2 className="h-4 w-4 text-[#3B82F6] mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="relative grid place-items-center">
                  {current.image ? (
                    <img
                      src={current.image}
                      alt={current.title}
                      className="w-full max-w-sm h-auto"
                      style={{
                        filter:
                          "drop-shadow(0 30px 35px rgba(6,16,42,0.22))",
                      }}
                    />
                  ) : (
                    <PlatformMock />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PlatformMock = () => (
  <div className="w-full max-w-sm rounded-2xl bg-white border border-[#06102A]/10 shadow-[0_30px_60px_-25px_rgba(6,16,42,0.25)] overflow-hidden">
    <div className="px-5 py-3 border-b border-[#06102A]/10 flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-full bg-[#06102A]/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#06102A]/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#06102A]/15" />
      <p className="ml-auto text-[10px] font-mono-vt text-[#06102A]/55">vaultavo · platform</p>
    </div>
    <div className="p-5 space-y-3">
      {[
        { l: "Treasury · Initiator", v: "M. Rivera" },
        { l: "Verifier", v: "K. Lin" },
        { l: "Approver", v: "C. Okafor" },
        { l: "Limit", v: "USD 2.5M" },
        { l: "Audit", v: "Hash 0x9a…fc" },
      ].map((r) => (
        <div
          key={r.l}
          className="flex items-center justify-between rounded-lg bg-[#06102A]/[0.03] px-4 py-3"
        >
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#06102A]/55">{r.l}</span>
          <span className="text-sm text-[#06102A]">{r.v}</span>
        </div>
      ))}
      <div className="pt-2 flex items-center justify-between text-xs text-[#06102A]/55">
        <span className="font-mono-vt">policy.passed</span>
        <span className="inline-flex items-center gap-2 text-[#06102A]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6]" /> awaiting hardware
        </span>
      </div>
    </div>
  </div>
);

/* ---------- 5. HOW IT WORKS ---------- */
const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      t: "Identity",
      icon: Fingerprint,
      d: "The user verifies their fingerprint on the Vaultavo Biometric Security Device, proving presence and intent.",
    },
    {
      n: "02",
      t: "Hardware",
      icon: Cpu,
      d: "The private key is generated and accessed inside isolated secure hardware. Never exposed. Never online.",
    },
    {
      n: "03",
      t: "Policy",
      icon: ShieldCheck,
      d: "The Digital Platform validates rules, approvals, limits, and workflows.",
    },
    {
      n: "04",
      t: "Ownership",
      icon: CheckCircle2,
      d: "Only when identity, hardware, and policy agree is the transaction signed and broadcast.",
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-[#FAFBFC]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          kicker="How It Works"
          title="Identity → Hardware → Policy → Ownership"
          sub="Vaultavo separates identity, keys, and control — then forces them to agree before anything can move."
        />

        <div className="mt-20 relative">
          {/* Connecting line */}
          <div
            className="hidden lg:block absolute left-0 right-0 top-12 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(6,16,42,0.2) 10%, rgba(59,130,246,0.4) 50%, rgba(249,115,22,0.4) 90%, transparent)",
            }}
          />
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 lg:gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 120}>
                <div className="relative">
                  <div className="relative h-24 w-24 rounded-2xl bg-white border border-[#06102A]/10 shadow-[0_15px_40px_-20px_rgba(6,16,42,0.3)] grid place-items-center">
                    <s.icon className="h-7 w-7 text-[#06102A]" strokeWidth={1.5} />
                    <span className="absolute -top-2 -right-2 h-6 px-2 rounded-full bg-[#06102A] text-white text-[10px] font-mono-vt grid place-items-center">
                      {s.n}
                    </span>
                  </div>
                  <h4 className="mt-6 font-display text-xl font-medium text-[#06102A]">{s.t}</h4>
                  <p className="mt-2 text-sm text-[#06102A]/70 leading-relaxed max-w-xs">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={250}>
          <p className="mt-16 text-sm md:text-base text-[#06102A]/60 max-w-3xl">
            No seed phrases. No cloud biometrics. No pooled custody. No access without the rightful
            owner.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- 6. UNHACKABLE ---------- */
const Unhackable = () => {
  const threats = [
    {
      icon: Target,
      title: "Phishing / Impersonation",
      defence: "No seed phrase to steal. Access is fingerprint-bound.",
    },
    {
      icon: Lock,
      title: "Theft / Loss",
      defence: "A stolen card is useless without the rightful user.",
    },
    {
      icon: AlertTriangle,
      title: "Coercion",
      defence: "Duress fingerprint flows and silent alert mechanisms.",
    },
    {
      icon: Network,
      title: "Systemic Hacks",
      defence: "Per-user hardware segregation prevents cascading breach.",
    },
  ];
  return (
    <section className="py-24 md:py-32 dark-panel relative overflow-hidden" data-testid="unhackable-section">
      <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
      <div
        className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(59,130,246,0.16) 0%, transparent 60%)",
        }}
      />
      <div
        className="absolute -bottom-40 -left-40 h-[400px] w-[400px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
        <SectionHeader
          dark
          kicker="Unhackable by Design"
          title="Most Systems Rely on Software. Vaultavo Relies on Physics."
          sub="To compromise a Vaultavo-protected asset, an attacker would need to breach the card, the vault, and the platform simultaneously — an event deemed practically impossible."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
          {threats.map((t, i) => (
            <Reveal key={t.title} delay={i * 90}>
              <div className="group rounded-2xl bg-white/[0.04] border border-white/10 p-7 backdrop-blur-md hover:bg-white/[0.07] hover:border-white/25 transition-all duration-500">
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-xl bg-white/10 grid place-items-center">
                    <t.icon className="h-5 w-5 text-white" strokeWidth={1.6} />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                    Vector 0{i + 1}
                  </span>
                </div>
                <h4 className="mt-6 font-display text-xl text-white font-medium">{t.title}</h4>
                <div className="mt-3 flex items-start gap-3">
                  <span className="mt-1 h-1 w-6 bg-[#F97316] rounded-full shrink-0" />
                  <p className="text-sm text-white/70 leading-relaxed">{t.defence}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- 7. COMPLIANCE ---------- */
const Compliance = () => {
  const items = [
    "Provable asset segregation",
    "Full audit trails",
    "Hardware-enforced policy",
    "KYC-linked identity",
    "Multi-party governance",
    "Institutional controls",
    "Regulatory alignment",
  ];
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="compliance-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <SectionHeader
            kicker="Compliance & Governance"
            title="Built for the Regulated Future"
            sub="Vaultavo delivers segregation, auditability, and non-repudiation natively."
          />
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <div className="rounded-3xl border border-[#06102A]/10 bg-white shadow-[0_30px_60px_-30px_rgba(6,16,42,0.2)] overflow-hidden">
              <div className="px-6 py-4 flex items-center justify-between border-b border-[#06102A]/10 bg-[#06102A]/[0.02]">
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                  Governance · Live Policy
                </p>
                <span className="inline-flex items-center gap-2 text-xs text-[#06102A]/55 font-mono-vt">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6]" /> active
                </span>
              </div>
              <ul className="divide-y divide-[#06102A]/[0.06]">
                {items.map((it, i) => (
                  <li
                    key={it}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-[#06102A]/[0.02] transition-colors"
                  >
                    <span className="font-mono-vt text-xs text-[#06102A]/45 w-8">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <FileText className="h-4 w-4 text-[#06102A]/55" />
                    <p className="text-[15px] text-[#06102A]">{it}</p>
                    <CheckCircle2 className="h-4 w-4 text-[#3B82F6] ml-auto" />
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------- 8. SOLUTIONS ---------- */
const Solutions = () => {
  const items = [
    {
      icon: Landmark,
      name: "Banks",
      pain: "Custody requirements that meet regulatory and audit demands.",
      vaultavo: "Identity-linked governance with provable asset segregation.",
    },
    {
      icon: Building2,
      name: "Exchanges",
      pain: "Hot wallets and pooled custody create systemic risk.",
      vaultavo: "Identity-bound transaction control and hardware-segregated custody.",
    },
    {
      icon: Coins,
      name: "Tokenization Platforms",
      pain: "Compliant custody for tokenized real-world assets.",
      vaultavo: "Lifecycle control with hardware and policy enforcement.",
    },
    {
      icon: Banknote,
      name: "Stablecoin Issuers",
      pain: "Operational integrity for mint, burn, and reserves.",
      vaultavo: "Identity-linked transaction control and asset protection.",
    },
    {
      icon: Briefcase,
      name: "Asset Managers",
      pain: "Protect client assets with auditable governance.",
      vaultavo: "Multi-party approvals with secure operational workflows.",
    },
    {
      icon: ShieldHalf,
      name: "Custodians",
      pain: "Differentiated, hardware-grade custody offering.",
      vaultavo: "Per-user hardware segregation with full audit trails.",
    },
    {
      icon: Globe,
      name: "Governments",
      pain: "Sovereign-grade infrastructure for digital ownership.",
      vaultavo: "Biometric trust layer with policy-controlled access.",
    },
    {
      icon: CircuitBoard,
      name: "CBDCs",
      pain: "Citizen-grade identity bound to digital currency.",
      vaultavo: "Identity-bound wallets and policy-governed disbursement.",
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-[#FAFBFC]" data-testid="solutions-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          kicker="Solutions"
          title="Infrastructure for Every Regulated Digital Asset Model"
          sub="Tailored configurations for banks, exchanges, tokenization platforms, stablecoin issuers, asset managers, custodians, governments, and CBDCs."
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((s, i) => (
            <Reveal key={s.name} delay={(i % 4) * 80}>
              <div className="group h-full rounded-2xl border border-[#06102A]/10 bg-white p-7 hover:border-[#06102A] hover:shadow-[0_30px_60px_-30px_rgba(6,16,42,0.25)] hover:-translate-y-1 transition-all duration-500">
                <s.icon className="h-6 w-6 text-[#06102A]" strokeWidth={1.6} />
                <h3 className="mt-6 font-display text-xl font-medium text-[#06102A]">{s.name}</h3>
                <div className="mt-5 space-y-3 text-[13px]">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#F97316]">Pain</p>
                    <p className="mt-1 text-[#06102A]/70 leading-relaxed">{s.pain}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6]">Vaultavo</p>
                    <p className="mt-1 text-[#06102A]/85 leading-relaxed">{s.vaultavo}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- 9. BEYOND DIGITAL ASSETS ---------- */
const Beyond = () => {
  const cases = [
    "eGovernment",
    "National ID",
    "Banking & payments",
    "Healthcare records",
    "Legal notarization",
    "Education credentials",
    "Access control",
    "Transportation",
    "Voting",
    "Welfare distribution",
  ];
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="beyond-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          kicker="Beyond Digital Assets"
          title="The Biometric Future of Digital Trust"
          sub="The same architecture that secures custody can secure identity, access, authority, and digital participation."
        />
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {cases.map((c, i) => (
            <Reveal key={c} delay={(i % 5) * 60}>
              <div className="group flex items-center gap-3 rounded-xl border border-[#06102A]/10 bg-white px-4 py-4 hover:border-[#06102A] transition-colors">
                <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6] group-hover:bg-[#F97316] transition-colors" />
                <span className="text-sm text-[#06102A]">{c}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- 10. TRACTION ---------- */
const Traction = () => {
  const blocks = [
    "Product, Platform, Vault, and Card fully developed and tested",
    "Manufacturing partner: CardLabs",
    "Second manufacturer being brought online",
    "Pilot opportunities across multiple global markets",
    "Three U.S. patent-pending filings",
  ];
  const awards = [
    "Blockchain Innovation Award · Asian Banking & Finance Fintech Awards 2025",
    "Accenture FinTech Innovation Lab Asia-Pacific · Finalist",
    "HICOOL Global Entrepreneur Summit · Semi-Finalist",
  ];
  return (
    <section className="py-24 md:py-32 bg-[#FAFBFC]" data-testid="traction-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <SectionHeader
            kicker="Traction & Credibility"
            title="From Prototype to Production-Ready Infrastructure"
          />
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
          {blocks.map((b, i) => (
            <Reveal key={b} delay={i * 70}>
              <div className="rounded-2xl bg-white border border-[#06102A]/10 p-6 h-full">
                <Activity className="h-5 w-5 text-[#06102A]/60" />
                <p className="mt-5 text-[15px] text-[#06102A] leading-relaxed">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-12 max-w-7xl mx-auto px-6 md:px-8">
        <Reveal>
          <div className="rounded-3xl border border-[#06102A]/10 bg-white p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {awards.map((a, i) => (
              <div key={a} className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-[#06102A] grid place-items-center">
                  <Award className="h-5 w-5 text-white" strokeWidth={1.6} />
                </div>
                <p className="text-sm text-[#06102A] leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- FAQ TEASER ---------- */
const FAQTeaser = () => {
  const items = [
    {
      q: "What is the Vaultavo Biometric Security Device?",
      a: "A hardware-secured biometric device that generates, stores, and authorizes cryptographic operations. Currently deployed as Vaultavo's flagship biometric smart card form factor.",
    },
    {
      q: "Are biometrics ever stored in the cloud?",
      a: "No. Biometric templates are stored and matched entirely within the EAL6+ secure element on the device — never in the cloud, never in centralized databases.",
    },
    {
      q: "How does Vaultavo compare to MPC and Fireblocks?",
      a: "Vaultavo is hardware-centric with biometric-bound authorization. MPC distributes cryptography across software; Vaultavo binds it to verified human presence inside isolated secure hardware.",
    },
    {
      q: "What form factors will Vaultavo support beyond the card?",
      a: "Authentication tokens, enterprise devices, embedded hardware, government identity devices, wearables, and specialised institutional custody hardware. The architecture is form-factor agnostic.",
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="faq-teaser-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            kicker="Frequently Asked"
            title="Answers for institutions evaluating Vaultavo."
            sub="Curated questions on devices, custody models, compliance, and how Vaultavo compares to existing custody architectures."
          />
          <Reveal delay={200}>
            <Link
              to="/faq"
              data-testid="faq-teaser-cta"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#06102A] vt-link"
            >
              View all questions
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={120}>
            <div className="glass-card rounded-2xl px-6 md:px-8">
              <Accordion type="single" collapsible defaultValue="q-0">
                {items.map((it, i) => (
                  <AccordionItem
                    key={i}
                    value={`q-${i}`}
                    className="border-b border-[#06102A]/[0.07] last:border-b-0"
                    data-testid={`home-faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-left text-[15px] md:text-base font-display font-medium text-[#06102A] hover:no-underline py-5 md:py-6">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pr-6 text-[#06102A]/75 leading-relaxed text-[15px]">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------- 11. FINAL CTA ---------- */
const FinalCTA = () => (
  <section className="py-28 md:py-40 bg-white relative overflow-hidden" data-testid="final-cta-section">
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(60% 50% at 50% 0%, rgba(59,130,246,0.10) 0%, transparent 70%), radial-gradient(40% 40% at 80% 100%, rgba(249,115,22,0.08) 0%, transparent 60%)",
      }}
    />
    <div className="max-w-7xl mx-auto px-6 md:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <Reveal>
          <Overline>The Vaultavo Standard</Overline>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A]">
            Digital Finance Cannot Scale Without Trust.
          </h2>
        </Reveal>
        <Reveal delay={150}>
          <p className="mt-7 text-lg text-[#06102A]/70 max-w-2xl leading-relaxed">
            Vaultavo is the identity-bound infrastructure layer for secure, compliant digital
            ownership.
          </p>
        </Reveal>
        <Reveal delay={220}>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              data-testid="final-cta-demo"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#06102A] text-white text-base font-medium px-7 py-4 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_18px_40px_-12px_rgba(6,16,42,0.4)]"
            >
              Request a Demo
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/contact"
              data-testid="final-cta-team"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#06102A] text-base font-medium px-7 py-4 border border-[#06102A]/15 hover:border-[#06102A] transition-all duration-300"
            >
              Speak to the Team
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-5 grid place-items-center">
        <Reveal delay={120}>
          <div className="relative">
            <FingerprintOrb size={300} />
            <Sparkles className="absolute top-2 right-2 h-4 w-4 text-[#F97316]" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- ROOT PAGE ---------- */
export default function Home() {
  return (
    <main className="bg-white" data-testid="home-page">
      <Hero />
      <Problem />
      <ParadigmShift />
      <Architecture />
      <InfrastructureVideo />
      <HowItWorks />
      <Unhackable />
      <Compliance />
      <Solutions />
      <Beyond />
      <Traction />
      <FAQTeaser />
      <FinalCTA />
    </main>
  );
}
